import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/** Static legal page — content in Italian, GDPR-compliant baseline. */
export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[color:var(--sp-subtle)] py-10 px-6">
      <div className="max-w-3xl mx-auto sp-card p-10">
        <Link to="/" className="inline-flex items-center gap-1 text-xs font-bold text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-brand)] mb-6">
          <ArrowLeft className="h-3 w-3" /> Torna a Memento
        </Link>
        <h1 className="font-display text-4xl font-black">Informativa sulla Privacy</h1>
        <p className="text-xs text-[color:var(--sp-text-muted)] mt-2">Ultimo aggiornamento: 7 febbraio 2026</p>

        <div className="prose prose-slate max-w-none mt-8 space-y-6 text-sm leading-relaxed">
          <section>
            <h2 className="font-display text-xl font-black">1. Titolare del trattamento</h2>
            <p>
              Il titolare del trattamento dei dati personali è <strong>Salon Up S.r.l.</strong> (di seguito
              &quot;Titolare&quot;, &quot;noi&quot; o &quot;Memento&quot;), P.IVA <strong>18474061001</strong>, con sede legale in
              <strong> Via del Pescheto 13, 00019 Tivoli (RM)</strong>. Per qualsiasi richiesta relativa alla protezione dei dati
              personali puoi scriverci a <a className="text-[color:var(--sp-brand)] font-bold" href="mailto:info@salon-up.it">info@salon-up.it</a>.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-black">2. Dati raccolti</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Dati di registrazione</strong>: email, nome, nome del salone, password (memorizzata in forma cifrata).</li>
              <li><strong>Dati operativi</strong>: anagrafica clienti (nome, email, telefono, note), appuntamenti, incassi, prodotti a magazzino.</li>
              <li><strong>Dati di comunicazione</strong>: messaggi WhatsApp/email inviati e ricevuti tramite la piattaforma.</li>
              <li><strong>Dati tecnici</strong>: indirizzo IP, tipo di browser, log di accesso.</li>
              <li><strong>Cookie tecnici</strong>: sessione di login (obbligatori per il funzionamento).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-black">3. Finalità e base giuridica</h2>
            <p>Trattiamo i dati per le seguenti finalità:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Erogazione del servizio</strong> (contratto, art. 6.1.b GDPR): gestione anagrafica, agenda, incassi.</li>
              <li><strong>Comunicazioni operative</strong> (contratto): reminder appuntamenti, conferme, richieste di feedback.</li>
              <li><strong>Marketing</strong> (consenso, art. 6.1.a GDPR): invio di promozioni via WhatsApp/email SOLO previo consenso esplicito del cliente finale.</li>
              <li><strong>Adempimenti fiscali e legali</strong> (obbligo di legge, art. 6.1.c GDPR): fatturazione, antiriciclaggio se applicabile.</li>
              <li><strong>Sicurezza e prevenzione frodi</strong> (legittimo interesse, art. 6.1.f GDPR).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-black">4. Conservazione dei dati</h2>
            <p>
              Conserviamo i dati personali per il tempo necessario alle finalità sopra descritte:
              dati operativi per la durata del contratto + 10 anni per obblighi fiscali;
              dati marketing fino a revoca del consenso; log tecnici per 12 mesi.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-black">5. Destinatari dei dati</h2>
            <p>I dati possono essere condivisi con:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Fornitori di infrastruttura cloud (hosting, database) sotto contratto DPA.</li>
              <li><strong>Meta Platforms Ireland Ltd.</strong> per l&apos;invio di messaggi WhatsApp Business API.</li>
              <li><strong>Resend Inc.</strong> per l&apos;invio di email transazionali e marketing.</li>
              <li>Consulenti fiscali e legali del Titolare, sotto obbligo di riservatezza.</li>
            </ul>
            <p className="mt-2">Alcuni destinatari possono avere sede fuori dall&apos;UE. In tal caso applichiamo Clausole Contrattuali Standard approvate dalla Commissione Europea.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-black">6. Diritti dell&apos;interessato</h2>
            <p>Ai sensi degli artt. 15-22 GDPR hai diritto di:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Accedere ai tuoi dati e ottenerne copia</li>
              <li>Chiedere la rettifica di dati inesatti</li>
              <li>Chiedere la cancellazione (&quot;diritto all&apos;oblio&quot;)</li>
              <li>Chiedere la limitazione del trattamento</li>
              <li>Ottenere la portabilità dei dati in formato leggibile</li>
              <li>Opporti al trattamento per marketing</li>
              <li>Proporre reclamo al Garante Privacy (<a className="text-[color:var(--sp-brand)] font-bold" href="https://www.garanteprivacy.it">garanteprivacy.it</a>)</li>
            </ul>
            <p className="mt-2">Per esercitare questi diritti scrivi a <a className="text-[color:var(--sp-brand)] font-bold" href="mailto:info@salon-up.it">info@salon-up.it</a>. Per la cancellazione dei dati puoi anche seguire le istruzioni alla pagina <a className="text-[color:var(--sp-brand)] font-bold" href="/data-deletion">Cancellazione dei dati</a>.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-black">7. WhatsApp Business e messaggistica</h2>
            <p>Memento utilizza la <strong>WhatsApp Business Platform</strong> di Meta per inviare e ricevere messaggi tra il salone e i suoi clienti finali. In questo contesto:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Inviamo promemoria appuntamenti, conferme, aggiornamenti operativi e — <strong>solo previo consenso esplicito</strong> — comunicazioni promozionali via WhatsApp.</li>
              <li>Puoi opporti in qualsiasi momento: <strong>rispondi &quot;STOP&quot;</strong> al messaggio WhatsApp per non ricevere più comunicazioni. La richiesta viene rispettata immediatamente e blocchiamo l&apos;invio di ulteriori messaggi proattivi.</li>
              <li><strong>Non condividiamo il tuo numero di telefono con terze parti</strong> per finalità di marketing.</li>
              <li><strong>Meta Platforms Ireland Ltd.</strong> agisce come responsabile del trattamento per la Cloud API e può conservare i messaggi per un massimo di 30 giorni ai soli fini del funzionamento del servizio.</li>
              <li>Puoi richiedere la cancellazione dei dati raccolti tramite WhatsApp seguendo le istruzioni alla pagina <a className="text-[color:var(--sp-brand)] font-bold" href="/data-deletion">Cancellazione dei dati</a>.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-black">8. Sicurezza</h2>
            <p>
              Adottiamo misure tecniche e organizzative adeguate (cifratura in transito HTTPS,
              password con bcrypt, isolamento multi-tenant, backup crittografati, controllo accessi
              basato sui ruoli) per proteggere i dati da accessi non autorizzati.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-black">9. Modifiche</h2>
            <p>
              Ci riserviamo di aggiornare questa informativa. Le modifiche sostanziali verranno
              comunicate via email o notifica in-app almeno 30 giorni prima dell&apos;entrata in vigore.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
